<template>
  <app-page class="vdv-page" fill lean :title="trip ? tripTitle : null" :subtitle="trip ? tripSubtitle : null">
    <!-- Übersicht wie die übrigen Tabellenseiten (Kundenwunsch 13.10.2026): leichter Titel, links die Suche; ohne Filter
         und Gruppierung stehen die Aktionen rechts in der Werkzeugleiste (Handy: Icon-Knöpfe im Kopf). Ein Klick auf
         eine Fahrt öffnet ihre Detailseite (?fahrt=…, Zurück im Browser führt zur Übersicht; Kundenwunsch 13.10.2026:
         „eine art neue seite … mit details also kopfzeilen und dann einzelnen haltestellen als tabelle. mit irgendwo
         einen button um zurück zur übersichtstabelle zu kommen“) -->
    <template v-if="handy" #actions>
      <template v-if="!trip">
        <app-icon-button icon="refresh" label="Aktualisieren" @click="refreshDataGrid" />
        <app-icon-button icon="download" label="Excel-Export" @click="exportToExcel" />
        <app-button icon="plus" @click="openAdditionalTripDialog">Zusatzfahrt</app-button>
      </template>
      <template v-else>
        <app-icon-button icon="refresh" label="Aktualisieren" @click="refreshDataGrid" />
        <app-icon-button icon="plus" label="Zusatzhalt anlegen" @click="openAdditionalStopDialog(trip.IstFahrt)" />
        <app-icon-button icon="x" label="Fahrtausfall melden" @click="cancelTrip(trip.IstFahrt)" />
      </template>
    </template>
    <template #toolbar>
      <app-search v-if="!trip" v-model="searchText" placeholder="In Fahrten suchen" @input="onSearchInput" />
      <app-button v-else icon="arrowLeft" @click="closeTrip">Zurück zur Übersicht</app-button>
    </template>
    <template v-if="!handy" #toolbar-end>
      <template v-if="!trip">
        <app-button icon="refresh" @click="refreshDataGrid">Aktualisieren</app-button>
        <app-button icon="download" @click="exportToExcel">Excel-Export</app-button>
        <app-button icon="plus" @click="openAdditionalTripDialog">Zusatzfahrt</app-button>
      </template>
      <template v-else>
        <app-button icon="refresh" @click="refreshDataGrid">Aktualisieren</app-button>
        <app-button icon="plus" @click="openAdditionalStopDialog(trip.IstFahrt)">Zusatzhalt anlegen</app-button>
        <!-- weiß wie „Stornieren“ bei Verkäufen (Kundenwunsch 13.10.2026: nicht rot) -->
        <app-button icon="x" @click="cancelTrip(trip.IstFahrt)">Fahrtausfall melden</app-button>
      </template>
    </template>

    <!-- Übersicht: bleibt beim Öffnen einer Fahrt erhalten (Suche, Seite und Scrollstand), nur ausgeblendet -->
    <div v-show="!trip" class="vdv-view">
      <!-- Tabelle füllt die Höhe über der Fußleiste (Handy: natürliche Höhe) -->
      <div class="app-grid-fill vdv-grid" :class="{ 'vdv-grid--auto': handy }">
        <dx-data-grid
          id="gridContainer"
          :ref="gridRefName"
          :height="gridHeight"
          :data-source="userDataSource"
          :columns="columns"
          :show-borders="false"
          :show-column-lines="false"
          :show-row-lines="true"
          :column-auto-width="true"
          :allow-column-reordering="!gridTouch"
          :allow-column-resizing="!gridTouch"
          :selection="selection"
          :hover-state-enabled="!gridTouch"
          :scrolling="scrolling"
          :element-attr="gridAttr"
          @toolbar-preparing="onToolbarPreparing"
          @row-click="onRowClick"
          @content-ready="onContentReady"
        >
          <dx-search-panel :visible="false" />

          <dx-paging :page-size="defaultPageSize" />
          <!-- Seitenwahl und Blättern in der Fußleiste (AppGridFooter) -->
          <dx-pager :visible="false" />

          <dx-export :enabled="false" />
        </dx-data-grid>
      </div>
      <app-grid-footer ref="fuss" :page-sizes="pageSizes">
        <template #summary>
          <span v-if="narrowed"><strong>{{ summaryCount }}</strong> von {{ summaryLoaded }}</span>
          <span v-else><strong>{{ summaryCount }}</strong> {{ summaryNoun }}</span>
          <template v-if="summary.finished">
            <span class="app-sep" aria-hidden="true">·</span>
            <span>davon <strong>{{ finishedCount }}</strong> beendet</span>
          </template>
        </template>
      </app-grid-footer>
    </div>

    <!-- Detailseite einer Fahrt: Kopfdaten, darunter die Halte als Tabelle (mit Durchfahrt und Ausfall je Halt) -->
    <div v-if="trip" class="vdv-view">
      <dl class="trip-facts">
        <div v-for="fact in tripFacts" :key="fact.label" class="trip-facts__item">
          <dt class="trip-facts__label">{{ fact.label }}</dt>
          <dd class="trip-facts__value">{{ fact.value }}</dd>
        </div>
      </dl>
      <div class="app-grid-fill vdv-grid" :class="{ 'vdv-grid--auto': handy }">
        <dx-data-grid
          ref="stopGrid"
          :height="gridHeight"
          :data-source="trip.IstFahrt.IstHalt"
          :columns="stopColumns(trip.IstFahrt)"
          :show-borders="false"
          :show-column-lines="false"
          :show-row-lines="true"
          :column-auto-width="true"
          :allow-column-reordering="!gridTouch"
          :allow-column-resizing="!gridTouch"
          :hover-state-enabled="!gridTouch"
          :scrolling="scrolling"
          :element-attr="stopGridAttr"
          @content-ready="onStopContentReady"
        >
          <dx-paging :page-size="defaultPageSize" />
          <dx-pager :visible="false" />
        </dx-data-grid>
      </div>
      <app-grid-footer ref="stopFuss" :page-sizes="pageSizes">
        <template #summary>
          <span><strong>{{ stopCount }}</strong> {{ stopNoun }}</span>
          <span class="app-sep" aria-hidden="true">·</span>
          <span>davon <strong>{{ reachedCount }}</strong> erreicht</span>
        </template>
      </app-grid-footer>
    </div>

    <app-dialog v-model="additionalStopDialog" title="Zusatzhalt anlegen" :max-width="600">
      <div class="app-form">
        <div class="app-form-field">
          <label class="app-label" for="actual-stop-position">Position</label>
          <app-text-field id="actual-stop-position" v-model="additionalStopPosition" type="number" />
        </div>
        <div class="app-form-field">
          <label class="app-label" for="actual-stop-id">Ortnummer / DHID</label>
          <app-text-field id="actual-stop-id" v-model="additionalStopId" />
        </div>
        <div class="app-form-field">
          <label class="app-label" for="actual-stop-time">Ankunft-/Abfahrtzeit</label>
          <app-text-field id="actual-stop-time" v-model="additionalStopTime" />
        </div>
      </div>
      <template #actions>
        <app-button @click="additionalStopDialog = false">Abbrechen</app-button>
        <app-button kind="primary" @click="addStop">Halt hinzufügen</app-button>
      </template>
    </app-dialog>
    <app-dialog v-model="additionalTripDialog" title="Zusatzfahrt anlegen" :max-width="600">
      <div class="app-form">
        <div class="app-form-field">
          <label class="app-label" for="actual-trip-id">Fahrt Kopie ID</label>
          <app-text-field id="actual-trip-id" v-model="additionalTripId" type="number" />
        </div>
        <div class="app-form-field">
          <label class="app-label" for="actual-trip-printer">Drucker ID</label>
          <app-text-field id="actual-trip-printer" v-model="additionalTripPrinterId" type="number" />
        </div>
      </div>
      <template #actions>
        <app-button @click="additionalTripDialog = false">Abbrechen</app-button>
        <app-button kind="primary" @click="addTrip">Fahrt hinzufügen</app-button>
      </template>
    </app-dialog>
    <app-dialog v-model="changeTrackDialog" title="Steigänderung" :max-width="600">
      <div class="app-form">
        <div class="app-form-field">
          <label class="app-label" for="actual-track-start">Ankunftssteig</label>
          <app-text-field id="actual-track-start" v-model="startTrack" />
        </div>
        <div class="app-form-field">
          <label class="app-label" for="actual-track-end">Abfahrtssteig</label>
          <app-text-field id="actual-track-end" v-model="endTrack" />
        </div>
      </div>
      <template #actions>
        <app-button @click="changeTrackDialog = false">Abbrechen</app-button>
        <app-button kind="primary" @click="changeTrack">Steigänderung melden</app-button>
      </template>
    </app-dialog>
  </app-page>
</template>

<script>
import {
  DxDataGrid,
  DxPaging,
  DxPager,
  DxSearchPanel,
  DxExport
} from "devextreme-vue/data-grid";
import CustomStore from "devextreme/data/custom_store";
import DataGridMixin from "@/mixins/DataGridMixin";
import axios from "axios";
import _ from "lodash";
import moment from "moment";
import { formatNumber, plural } from "@/utils/format";

// Uhrzeit einer Zeitangabe der Schnittstelle („10:00“ bzw. mit Format)
function timeText(value, format) {
  const m = value ? moment(value) : null;
  return m && m.isValid() ? m.format(format || "HH:mm") : null;
}

// „2/4“: zuletzt real erreichte Haltestelle von allen
function progressText(ist) {
  const stops = Array.isArray(ist.IstHalt) ? ist.IstHalt : [];
  return (_.findLastIndex(stops, ["IstAnkunftPrognoseStatus", "Real"]) + 1) + "/" + stops.length;
}

export default {
  components: {
    DxDataGrid,
    DxPaging,
    DxPager,
    DxSearchPanel,
    DxExport
  },
  mixins: [DataGridMixin],
  watch: {
    // zurück in der Übersicht: das ausgeblendete Grid neu vermessen
    trip(value) {
      if (!value) {
        this.$nextTick(() => {
          const ref = this.$refs[this.gridRefName];
          if (ref && ref.instance) {
            ref.instance.updateDimensions();
          }
        });
      }
    }
  },
  computed: {
    handy() {
      return !!(this.$dichte && this.$dichte.handy);
    },
    // Handy: AppPage fill wirkt dort nicht, das Grid wächst mit seinen Zeilen; sonst füllt es die Höhe
    gridHeight() {
      return this.handy ? undefined : "100%";
    },
    // Fußleiste: Anzahl nach der Suche, davon beendet
    narrowed() {
      return this.summary.count !== this.summary.loaded;
    },
    summaryCount() {
      return formatNumber(this.summary.count);
    },
    summaryLoaded() {
      return formatNumber(this.summary.loaded) + " " + plural(this.summary.loaded, "Fahrt", "Fahrten");
    },
    summaryNoun() {
      return plural(this.summary.count, "Fahrt", "Fahrten");
    },
    finishedCount() {
      return formatNumber(this.summary.finished);
    },
    // gewählte Fahrt (?fahrt=_id) aus den geladenen Fahrten; null = Übersicht
    trip() {
      const id = this.$route && this.$route.query ? this.$route.query.fahrt : null;
      return id ? this.trips.find(row => row && String(row._id) === String(id)) || null : null;
    },
    tripTitle() {
      const ist = this.trip.IstFahrt;
      const id = ist.FahrtRef && ist.FahrtRef.FahrtID ? ist.FahrtRef.FahrtID.FahrtBezeichner : null;
      return "Fahrt " + (id || "–");
    },
    // „Linie 6002 · 10:00–11:00 Uhr“
    tripSubtitle() {
      const ist = this.trip.IstFahrt;
      const se = (ist.FahrtRef && ist.FahrtRef.FahrtStartEnde) || {};
      const times = [timeText(se.Startzeit), timeText(se.Endzeit)].filter(Boolean).join("–");
      return ["Linie " + (ist.LinienID || "–"), times ? times + " Uhr" : null].filter(Boolean).join(" · ");
    },
    tripFacts() {
      const ist = this.trip.IstFahrt;
      const ref = ist.FahrtRef || {};
      const se = ref.FahrtStartEnde || {};
      const meta = ist._metadata || {};
      return [
        { label: "Linie", value: ist.LinienID || "–" },
        { label: "Richtung", value: ist.RichtungsID || "–" },
        { label: "Fahrt", value: (ref.FahrtID && ref.FahrtID.FahrtBezeichner) || "–" },
        { label: "Betriebstag", value: ref.FahrtID && ref.FahrtID.Betriebstag ? moment(ref.FahrtID.Betriebstag).format("DD.MM.YYYY") : "–" },
        { label: "Start", value: timeText(se.Startzeit, "HH:mm:ss") || "–" },
        { label: "Ende", value: timeText(se.Endzeit, "HH:mm:ss") || "–" },
        { label: "Haltestelle", value: progressText(ist) },
        { label: "Beendet", value: meta.finished === true ? "Ja" : "Nein" },
        { label: "Rufbus", value: meta.call_bus === true ? "Ja" : "Nein" }
      ];
    },
    stopList() {
      return this.trip && Array.isArray(this.trip.IstFahrt.IstHalt) ? this.trip.IstFahrt.IstHalt : [];
    },
    stopCount() {
      return formatNumber(this.stopList.length);
    },
    stopNoun() {
      return plural(this.stopList.length, "Halt", "Halte");
    },
    reachedCount() {
      return formatNumber(this.stopList.filter(stop => stop && stop.IstAnkunftPrognoseStatus === "Real").length);
    },
    columns() {
      return [
        {
          dataField: "IstFahrt.FahrtRef.FahrtStartEnde.Startzeit",
          caption: "Startzeit",
          dataType: "date",
          sortIndex: 1,
          sortOrder: "asc",
          format: "HH:mm:ss"
        },
        {
          dataField: "IstFahrt.FahrtRef.FahrtStartEnde.Endzeit",
          caption: "Endzeit",
          dataType: "date",
          format: "HH:mm:ss"
        },
        {
          dataField: "IstFahrt.LinienID",
          caption: "Linie"
        },
        {
          dataField: "IstFahrt.RichtungsID",
          caption: "Richtung"
        },
        {
          dataField: "IstFahrt.FahrtRef.FahrtID.FahrtBezeichner",
          caption: "Fahrt"
        },
        {
          dataField: "IstFahrt.IstHalt",
          caption: "Haltestelle",
          calculateDisplayValue: function(rowData) {
            const index =
              _.findLastIndex(rowData.IstFahrt.IstHalt, [
                "IstAnkunftPrognoseStatus",
                "Real"
              ]) + 1;
            return `${index}/${rowData.IstFahrt.IstHalt.length}`;
          }
        },
        {
          dataField: "IstFahrt._metadata.finished",
          caption: "Beendet"
        },
        {
          dataField: "IstFahrt._metadata.call_bus",
          caption: "Rufbus"
        }
      ];
    }
  },
  data() {
    return {
      gridRefName: "actualDataGrid",
      searchText: "",
      summary: { count: 0, loaded: 0, finished: 0 },
      // geladene Fahrten (eingefroren): Übersicht und Detailseite
      trips: [],
      // Zeiger auf den Zeilen: ein Klick öffnet die Detailseite der Fahrt
      gridAttr: { class: "app-grid--detail" },
      stopGridAttr: { class: "trip-stops" },
      selection: { mode: "single" },
      userDataSource: new CustomStore({
        key: ["_id"],
        load: () => axios.get(`${this.$store.state.baseUrl}vdvactualdata`).then(response => {
          this._rows = Array.isArray(response.data) ? response.data : [];
          this.trips = Object.freeze(this._rows.slice());
          return response;
        })
      }),
      additionalStopDialog: false,
      additionalStopPosition: 0,
      additionalStopId: "1190503",
      additionalStopTime: "2020-08-11T10:24:00Z",
      additionalStopIstFahrt: null,
      additionalTripDialog: false,
      additionalTripId: null,
      additionalTripPrinterId: null,
      changeTrackDialog: false,
      startTrack: null,
      endTrack: null,
      changeTrackIstFahrt: null,
      changeTrackStop: null
    };
  },
  methods: {
    onToolbarPreparing(e) {
      // Remove all default toolbar items; die leere Leiste ganz ausblenden (sonst zweite Linie über dem Tabellenkopf)
      e.toolbarOptions.items = [];
      e.toolbarOptions.visible = false;
    },
    // Detailseite einer Fahrt öffnen bzw. schließen (in der Adresse, damit Zurück im Browser zur Übersicht führt)
    onRowClick(e) {
      if (e.rowType === "data" && e.data && e.data._id !== undefined) {
        this.$router.push({ query: Object.assign({}, this.$route.query, { fahrt: String(e.data._id) }) });
      }
    },
    closeTrip() {
      const query = Object.assign({}, this.$route.query);
      delete query.fahrt;
      this.$router.push({ query });
    },
    onStopContentReady(e) {
      if (this.$refs.stopFuss) {
        this.$refs.stopFuss.update(e.component);
      }
    },
    onContentReady(e) {
      const grid = e.component;
      const count = Math.max(Number(grid.totalCount()) || 0, 0);
      const rows = this._rows || [];
      const finished = rows.filter(row => row && row.IstFahrt && row.IstFahrt._metadata && row.IstFahrt._metadata.finished === true).length;
      this.summary = { count, loaded: Math.max(rows.length, count), finished: this.searchText ? 0 : finished };
      if (this.$refs.fuss) {
        this.$refs.fuss.update(grid);
      }
    },
    onSearchInput() {
      this.$refs[this.gridRefName].instance.searchByText(this.searchText || "");
    },
    exportToExcel() {
      this.$refs[this.gridRefName].instance.exportToExcel(false);
    },
    refreshDataGrid() {
      this.$refs[this.gridRefName].instance.refresh();
    },
    openAdditionalStopDialog(IstFahrt) {
      this.additionalStopDialog = true;
      this.additionalStopIstFahrt = IstFahrt;
    },
    openAdditionalTripDialog() {
      this.additionalTripDialog = true;
    },
    async passage(event, IstFahrt) {
      const stop = event.row.data;
      // console.log({ stop });
      // console.log({ event });
      // console.log({ IstFahrt });
      await axios.post(
        `${this.$store.state.baseUrl}vdvtripedit?type=passage&customer_id=${IstFahrt._metadata.customer_id}&FahrtBezeichner=${IstFahrt.FahrtRef.FahrtID.FahrtBezeichner}&Betriebstag=${IstFahrt.FahrtRef.FahrtID.Betriebstag}&HaltID=${stop.HaltID}&Ankunftszeit=${stop.Ankunftszeit}&Abfahrtszeit=${stop.Abfahrtszeit}`
      );
      this.refreshDataGrid();
    },
    async openChangeTrackDialog(event, IstFahrt) {
      const stop = event.row.data;
      // console.log({ stop });
      // console.log({ event });
      // console.log({ IstFahrt });
      this.changeTrackDialog = true;
      this.changeTrackIstFahrt = IstFahrt;
      this.changeTrackStop = stop;
    },
    async changeTrack() {
      await axios.post(
        `${this.$store.state.baseUrl}vdvtripedit?type=changeTrack&customer_id=${this.changeTrackIstFahrt._metadata.customer_id}&FahrtBezeichner=${this.changeTrackIstFahrt.FahrtRef.FahrtID.FahrtBezeichner}&Betriebstag=${this.changeTrackIstFahrt.FahrtRef.FahrtID.Betriebstag}&HaltID=${this.changeTrackStop.HaltID}&Ankunftszeit=${this.changeTrackStop.Ankunftszeit}&Abfahrtszeit=${this.changeTrackStop.Abfahrtszeit}&AnkunftssteigText=${this.startTrack}&AbfahrtssteigText=${this.endTrack}`
      );
      this.changeTrackDialog = false;
      this.refreshDataGrid();
    },
    async removeStop(event, IstFahrt) {
      const stop = event.row.data;
      // console.log({ stop });
      // console.log({ event });
      // console.log({ IstFahrt });
      await axios.post(
        `${this.$store.state.baseUrl}vdvtripedit?type=removeStop&customer_id=${IstFahrt._metadata.customer_id}&FahrtBezeichner=${IstFahrt.FahrtRef.FahrtID.FahrtBezeichner}&Betriebstag=${IstFahrt.FahrtRef.FahrtID.Betriebstag}&HaltID=${stop.HaltID}&Ankunftszeit=${stop.Ankunftszeit}&Abfahrtszeit=${stop.Abfahrtszeit}`
      );
      this.refreshDataGrid();
    },
    async cancelTrip(IstFahrt) {
      await axios.post(
        `${this.$store.state.baseUrl}vdvtripedit?type=cancelTrip&customer_id=${IstFahrt._metadata.customer_id}&FahrtBezeichner=${IstFahrt.FahrtRef.FahrtID.FahrtBezeichner}&Betriebstag=${IstFahrt.FahrtRef.FahrtID.Betriebstag}`
      );
      this.refreshDataGrid();
    },
    async addStop() {
      // console.log(this.additionalStopIstFahrt);
      // console.log(this.additionalStopPosition);
      // console.log(this.additionalStopId);
      // console.log(this.additionalStopTime);
      await axios.post(
        `${this.$store.state.baseUrl}vdvtripedit?type=additionalStop&customer_id=${this.additionalStopIstFahrt._metadata.customer_id}&FahrtBezeichner=${this.additionalStopIstFahrt.FahrtRef.FahrtID.FahrtBezeichner}&Betriebstag=${this.additionalStopIstFahrt.FahrtRef.FahrtID.Betriebstag}&HaltID=${encodeURIComponent(this.additionalStopId)}&position=${this.additionalStopPosition}&Ankunftszeit=${this.additionalStopTime}&Abfahrtszeit=${this.additionalStopTime}`
      );
      this.additionalStopDialog = false;
      this.refreshDataGrid();
    },
    async addTrip() {
      // console.log(this.additionalTripId);
      // console.log(this.additionalTripPrinterId);
      await axios.post(
        `${this.$store.state.baseUrl}vdvtripedit?type=additionalTrip&customer_id=${this.$store.state.customerId}&FahrtBezeichner=${this.additionalTripId}&printer_id=${this.additionalTripPrinterId}`
      );
      this.additionalTripDialog = false;
      this.refreshDataGrid();
    },

    stopColumns(IstFahrt) {
      return [
        {
          type: "buttons",
          caption: "Aktionen",
          name: "actions",
          buttons: [
            {
              hint: "Durchfahrt",
              icon: "arrowright",
              visible: true,
              onClick: async e => await this.passage(e, IstFahrt)
            },
            // {
            //   hint: "Steigänderung",
            //   icon: "columnfield",
            //   visible: true,
            //   onClick: async e => await this.openChangeTrackDialog(e, IstFahrt)
            // },
            {
              hint: "Ausfall",
              icon: "trash",
              visible: true,
              onClick: async e => await this.removeStop(e, IstFahrt)
            }
          ]
        },
        {
          caption: "Status",
          calculateCellValue: function(rowData) {
            return rowData.Durchfahrt === true ? "Durchfahrt" : "";
          }
        },
        {
          dataField: "_metadata.order_nr",
          caption: "Reihenfolge"
          // sortIndex: 1,
          // sortOrder: "asc"
        },
        {
          dataField: "HaltID",
          caption: "Haltestelle"
        },
        {
          dataField: "_metadata.name",
          caption: "Bezeichnung"
        },
        {
          dataField: "Ankunftszeit",
          caption: "Ankunftszeit",
          sortIndex: 1,
          sortOrder: "asc",
          calculateDisplayValue: rowData => {
            let suffix = "";
            if (rowData.IstAnkunftPrognose) {
              const diff = moment(rowData.IstAnkunftPrognose).diff(
                moment(rowData.Ankunftszeit),
                "seconds"
              );
              if (diff >= 0) {
                suffix = `(+${Math.abs(Math.ceil(diff / 60))}min)`;
              } else {
                suffix = `(-${Math.abs(Math.ceil(diff / 60))}min)`;
              }
            }

            return `${moment(rowData.Ankunftszeit).format(
              "HH:mm:ss"
            )} ${suffix}`;
          }
        },
        {
          dataField: "Abfahrtszeit",
          caption: "Abfahrtszeit",
          calculateDisplayValue: rowData => {
            let suffix = "";
            if (rowData.IstAbfahrtPrognose) {
              const diff = moment(rowData.IstAbfahrtPrognose).diff(
                moment(rowData.Abfahrtszeit),
                "seconds"
              );
              if (diff >= 0) {
                suffix = `(+${Math.abs(Math.ceil(diff / 60))}min)`;
              } else {
                suffix = `(-${Math.abs(Math.ceil(diff / 60))}min)`;
              }
            }

            return `${moment(rowData.Abfahrtszeit).format(
              "HH:mm:ss"
            )} ${suffix}`;
          }
        },
        {
          dataField: "IstAnkunftPrognose",
          caption: "Prognose Ankunft",
          dataType: "date",
          format: "HH:mm:ss"
        },
        {
          dataField: "IstAnkunftPrognoseStatus",
          caption: "Status Ankunft"
        },
        {
          dataField: "IstAbfahrtPrognose",
          caption: "Prognose Abfahrt",
          dataType: "date",
          format: "HH:mm:ss"
        },
        {
          dataField: "IstAbfahrtPrognoseStatus",
          caption: "Status Abfahrt"
        }
      ];
    }
  }
};
</script>

<style scoped>
/* Handy: Grid in natürlicher Höhe */
.vdv-grid.vdv-grid--auto {
  flex: none;
}
/* Übersicht und Detailseite: je eine Spalte aus Tabelle und Fußleiste (füllt das Blatt) */
.vdv-view {
  flex: 1 1 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.vdv-page.app-page--natural .vdv-view {
  flex: none;
}
/* Kopfdaten der Fahrt: Bezeichnung über Wert, in einer umbrechenden Zeile unter der Werkzeugleiste */
.trip-facts {
  flex: none;
  display: flex;
  flex-wrap: wrap;
  gap: 12px 32px;
  margin: 0;
  padding: 12px var(--page-x, 20px) 16px;
  border-bottom: 1px solid var(--line);
}
.trip-facts__item {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.trip-facts__label {
  font-size: 12px;
  line-height: 16px;
  color: var(--ink-3);
}
.trip-facts__value {
  margin: 0;
  font-size: 13px;
  line-height: 20px;
  font-weight: 500;
  color: var(--ink);
  font-variant-numeric: tabular-nums;
}
/* Aktionen je Halt (Durchfahrt, Ausfall) leiser als die Daten, bei Hover kräftig */
.trip-stops ::v-deep .dx-command-edit .dx-link {
  color: var(--ink-3);
}
:where(html:not(.dichte-touch)) .trip-stops ::v-deep .dx-command-edit .dx-link:hover {
  color: var(--ink);
}
</style>
